/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-08 17:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';

import React, { Component } from 'react';
import {
  Card,
  Icon,
  Heading,
  TextStyle,
  Badge,
  Button,
  SkeletonPage,
  Layout,
  SkeletonBodyText
} from '@shopify/polaris';
import { ThemesMajor, StarFilledMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { Carousel } from 'src/components/views/common/carousel';
import * as dashboardV3Action from 'actions/dashboard-v3-actions';

import UnionIcon from 'src/assets/images/common/union.svg';

import history from 'src/routes/history';
import { routes } from 'src/routes';
import { CROSS_SELL_UP_SELL } from 'src/constants/homepage';
class CrossSellUpSell extends Component<WithI18nProps> {
  componentDidMount() {
    this.props.getCrossSellUpSellList();
  }
  render() {
    const { i18n, features } = this.props;
    let elements = [];
    if (!features || !features.length) {
      elements = this.defaultSkeletonElements();
    } else {
      elements = this.buildCSUSElements(i18n, features);
    }

    return (
      <div className="BC-CrossSellUpSell__DashboardV3">
        <Card sectioned>
          <Heading>{i18n.translate('AdminHTML.DashboardV3.CrossSellUpSell.title')}</Heading>
          <TextStyle variation="subdued">
            {i18n.translate('AdminHTML.DashboardV3.CrossSellUpSell.subtext')}
          </TextStyle>
          <div className="feature-list">
            <Carousel items={elements} />
          </div>
        </Card>
      </div>
    );
  }

  goToFeaturePage = name => {
    switch (name) {
      case 'Analytics':
        history.push(routes.analytics.appImpact());
        return;
      case 'Merchandising':
        history.push(routes.merchandising.view());
        return;
      case 'Recommendation':
        history.push(routes.recommendationWidgets.list());
        return;
      case 'SwatchSettings':
        history.push(routes.swatchSettings.view());
        return;
      case 'InstantSearchDisplay':
        history.push(routes.instantSearchWidget.view());
        return;
      case 'ManageFilterTree':
        history.push(routes.filterTree.list());
        return;
      case 'AISynonyms':
        history.push(routes.synonym.view(), { tab: 1 });
        return;
      case 'FilterSettings':
        history.push(routes.filterSettings.view());
        return;
      default:
        return;
    }
  };

  goToOtherPage = name => {
    switch (name) {
      case 'addon-store':
        history.push(routes.subscription.addon());
        return;
      default:
        return;
    }
  };

  defaultSkeletonElements = () => {
    const elements = [];
    for (let index = 0; index < 2; index++) {
      elements.push(
        <div className={`card card-feature`}>
          <SkeletonPage primaryAction fullWidth>
            <Layout>
              <Layout.Section oneHalf={true}>
                <Card sectioned>
                  <SkeletonBodyText lines={4} />
                </Card>
              </Layout.Section>
            </Layout>
          </SkeletonPage>
        </div>
      );
    }

    return elements;
  };

  buildCSUSElements = (i18n: any, features: any) => {
    const { notAllowAddon } = this.props;
    if (features.length < 6) features.push({ type: 'other', name: 'addon-store' });

    const elements = features.map(feature => {
      const type = feature.type === 'add-on' ? 'addon' : feature.type;
      const name = feature.name;
      const remainingDays = feature.remainingDays;
      const onClickAddon = () => {
        history.push(routes.subscription.addon(), {
          addOnName: CROSS_SELL_UP_SELL.addon[name],
          analyticDays: CROSS_SELL_UP_SELL.addon[name] === 'analytics' ? name.split('-')[1] : ''
        });
      };
      return (
        <div className={`card card-${type}`}>
          {type === 'other' ? (
            <>
              <img src={UnionIcon} alt="" />
              <h1>
                {i18n.translate(`AdminHTML.DashboardV3.CrossSellUpSell.${type}.${name}_Label`)}
              </h1>
              <TextStyle variation="subdued">
                {i18n.translate(`AdminHTML.DashboardV3.CrossSellUpSell.${type}.${name}_Subtext`)}
              </TextStyle>

              <div className="action">
                <Button onClick={() => this.goToOtherPage(name)}>
                  {i18n.translate(`AdminHTML.DashboardV3.CrossSellUpSell.${type}.${name}_Action`)}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Badge status={type === 'addon' ? 'warning' : 'info'}>
                <Icon source={type === 'addon' ? StarFilledMinor : ThemesMajor} />
                {i18n.translate(`AdminHTML.DashboardV3.CrossSellUpSell.${type}.label`)}
              </Badge>
              <h2>
                {i18n.translate(`AdminHTML.DashboardV3.CrossSellUpSell.${type}.${name}_Label`)}
              </h2>
              {(type === 'addon' && name.includes('analytics')) ||
              (type === 'feature' && name === 'Recommendation') ? (
                <TextStyle variation="subdued">
                  {i18n.translate(`AdminHTML.DashboardV3.CrossSellUpSell.${type}.${name}_Subtext`, {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          `AdminHTML.DashboardV3.CrossSellUpSell.${type}.${name}_Subtext_01`
                        )}
                      </TextStyle>
                    )
                  })}
                </TextStyle>
              ) : (
                <TextStyle variation="subdued">
                  {i18n.translate(`AdminHTML.DashboardV3.CrossSellUpSell.${type}.${name}_Subtext`)}
                </TextStyle>
              )}
              {type === 'addon' &&
                remainingDays > 0 &&
                !name.includes('analytics') &&
                !name.includes('sync-frequency') && (
                  <p className="sub-text">
                    {i18n.translate('AdminHTML.DashboardV3.CrossSellUpSell.addon.subtext', {
                      number: remainingDays
                    })}
                  </p>
                )}
              <div className="action">
                {type === 'addon' ? (
                  <Button disabled={notAllowAddon} onClick={onClickAddon}>
                    {i18n.translate(
                      `AdminHTML.DashboardV3.CrossSellUpSell.addon.${name}${
                        remainingDays > 0 && !name.includes('analytics') ? '_Free' : ''
                      }_Action`
                    )}
                  </Button>
                ) : (
                  <Button onClick={() => this.goToFeaturePage(name)}>
                    {i18n.translate(`AdminHTML.DashboardV3.CrossSellUpSell.feature.${name}_Action`)}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      );
    });

    return elements;
  };
}

const mapStateToProps = (state, props) => {
  return {
    notAllowAddon: _.get(state.oauth, 'subscription.notAllowAddon', false),
    features: _.get(state.dashboardV3, 'features', {}),
    gettingFeatures: _.get(state.dashboardV3, 'gettingFeatures', {})
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getCrossSellUpSellList: () => dispatch(dashboardV3Action.getCrossSellUpSellList(ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(CrossSellUpSell));
